import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';

// Components
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import ProtectedRoute from './components/ProtectedRoute';
import AdminRoute from './components/AdminRoute';
import AdminLayout from './components/AdminLayout';
import MaintenanceGuard from './components/MaintenanceGuard';

// Pages (Public/User)
import Home from './pages/Home';
import About from './pages/About';
import Documentation from './pages/Documentation';
import Changelog from './pages/Changelog';
import Contact from './pages/Contact';
import PrivacyPolicy from './pages/PrivacyPolicy';
import Terms from './pages/Terms';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import PublicProfile from './pages/PublicProfile';
import EmbedWidget from './pages/EmbedWidget';
import AdminRegister from './pages/AdminRegister';
import Resume from './pages/Resume';
import WhatIsNew from './pages/WhatIsNew';
import FAQ from './pages/FAQ';

// Pages (Admin)
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminUsers from './pages/admin/AdminUsers';
import AdminProfiles from './pages/admin/AdminProfiles';
import AdminMessages from './pages/admin/AdminMessages';
import AdminLogs from './pages/admin/AdminLogs';
import AdminSettings from './pages/admin/AdminSettings';
import AdminStatistics from './pages/admin/AdminStatistics';
import AdminAdmins from './pages/admin/AdminAdmins';
import AdminDocumentation from './pages/admin/AdminDocumentation';
import AdminReports from './pages/admin/AdminReports';
import AdminChangelog from './pages/admin/AdminChangelog';
import AdminNotifications from './pages/admin/AdminNotifications';
import AdminAIHub from './pages/admin/AdminAIHub';
import AdminSecurityReview from './pages/admin/AdminSecurityReview';
import AdminConsultations from './pages/admin/AdminConsultations';
import AdminErrors from './pages/admin/AdminErrors';
import AdminSitemap from './pages/admin/AdminSitemap';
import AdminIdentitySearch from './pages/admin/AdminIdentitySearch';

// New Systems
import VerificationPortal from './pages/VerificationPortal';
import NotFound from './pages/NotFound';

// Components
import NotificationCenter from './components/NotificationCenter';
import DevTrackerAssistant from './components/DevTrackerAssistant';
import SystemUpdateBanner from './components/SystemUpdateBanner';
import ErrorBoundary from './components/ErrorBoundary';

// User Restricted
import Suspended from './pages/Suspended';
import Report from './pages/Report';

import { useAuth } from './context/AuthContext';

function AppContent() {
  const { userProfile } = useAuth();

  return (
    <Router>
      <SystemUpdateBanner />
      <MaintenanceGuard>
        <ErrorBoundary userId={userProfile?.suid || userProfile?.adminId}>
          <div className="app-layout">
            <Routes>
              {/* --- ADMIN PANEL --- */}
              <Route path="/admin" element={
                <AdminRoute>
                  <AdminLayout />
                </AdminRoute>
              }>
                <Route path="dashboard" element={<AdminDashboard />} />
                <Route path="users" element={<AdminUsers />} />
                <Route path="profiles" element={<AdminProfiles />} />
                <Route path="admins" element={<AdminAdmins />} />
                <Route path="statistics" element={<AdminStatistics />} />
                <Route path="messages" element={<AdminMessages />} />
                <Route path="logs" element={<AdminLogs />} />
                <Route path="docs" element={<AdminDocumentation />} />
                <Route path="reports" element={<AdminReports />} />
                <Route path="changelog" element={<AdminChangelog />} />
                <Route path="notifications" element={<AdminNotifications />} />
                <Route path="ai-intel" element={<AdminAIHub />} />
                <Route path="security-review" element={<AdminSecurityReview />} />
                <Route path="identity-search" element={<AdminIdentitySearch />} />
                <Route path="consultations" element={<AdminConsultations />} />
                <Route path="settings" element={<AdminSettings />} />
                <Route path="errors" element={<AdminErrors />} />
                <Route path="register" element={<AdminRegister />} />
                <Route path="sitemap" element={<AdminSitemap />} />
              </Route>

              {/* --- RAW ROUTES --- */}
              <Route path="/embed/:profileId" element={<EmbedWidget />} />
              <Route path="/resume/:profileId" element={<Resume />} />
              
              <Route path="*" element={
                <>
                  <Navbar />
                  <main className="page-container">
                    <Routes>
                      <Route path="/" element={<Home />} />
                      <Route path="/about" element={<About />} />
                      <Route path="/docs" element={<Documentation />} />
                      <Route path="/changelog" element={<Changelog />} />
                      <Route path="/changelog/page/:pageNumber" element={<Changelog />} />
                      <Route path="/contact" element={<Contact />} />
                      <Route path="/privacy" element={<PrivacyPolicy />} />
                      <Route path="/terms" element={<Terms />} />
                      <Route path="/whats-new" element={<WhatIsNew />} />
                      <Route path="/whats-new/:version" element={<WhatIsNew />} />
                      <Route path="/sitemap" element={<AdminSitemap />} />
                      <Route path="/faq" element={<FAQ />} />
                      <Route path="/dev/:profileId" element={<PublicProfile />} />
                      <Route path="/verify/:profileId" element={<VerificationPortal />} />
                      <Route path="/login" element={<Login />} />
                      <Route path="/register" element={<Register />} />
                      <Route 
                        path="/dashboard" 
                        element={<ProtectedRoute><Dashboard /></ProtectedRoute>} 
                      />
                      <Route 
                        path="/suspended" 
                        element={<ProtectedRoute><Suspended /></ProtectedRoute>} 
                      />
                      <Route 
                        path="/report" 
                        element={<ProtectedRoute><Report /></ProtectedRoute>} 
                      />
                      <Route path="*" element={<NotFound />} />
                    </Routes>
                  </main>
                  <Footer />
                </>
              } />
            </Routes>
            <NotificationCenter />
            <DevTrackerAssistant />
          </div>
        </ErrorBoundary>
      </MaintenanceGuard>
    </Router>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
